import useI18n from '@/shared/hooks/usei18n'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { AggregatedProjection } from '@/features/cbam-reporting/hooks/cbamLiability'
import { computeCbamLiabilityEuro } from '@/shared/utils/computeCbamLiabilityEuro'
import {
  computeLiabilityLowerBound,
  computeLiabilityUpperBound
} from '@/shared/utils/computeCbamLiabilityBounds'
import CaretDown from '@/common/components/icons/CaretDown/CaretDown'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import cx from 'classnames'

function EmissionsRow({ label, value }: { label: string; value: number }) {
  const { numberFormatter3Decimals } = useI18n()
  return (
    <div className="flex items-center">
      <div className="px-4 py-2 w-1/2 text-right font-bold text-gray-600">
        {value > 0 ? `${numberFormatter3Decimals(value)} tCO₂` : '-'}
      </div>
      <div className="bg-blue-200 px-4 py-2 w-1/2">
        <span className="text-sm text-text-secondary">{label}</span>
      </div>
    </div>
  )
}

function LiabilityBoundsRow({
  formattedLiability,
  label,
  type,
  percentageValue
}: {
  formattedLiability: string
  label: string
  type: 'high' | 'low'
  percentageValue: number
}) {
  const percentageText =
    type === 'high' ? `+${percentageValue * 100}%` : `-${percentageValue * 100}%`
  return (
    <div className="flex items-center">
      <div className="w-1/2 text-right px-4 py-2">
        <span className="font-bold text-gray-600">{formattedLiability}</span>
      </div>
      <div className="w-1/2 flex px-4 py-2 items-center">
        <span className="hidden 2xl:inline text-sm text-text-secondary mr-2">{label}</span>
        <div className="w-1/2 flex items-center justify-end">
          <CaretDown
            className={cx(
              'size-8',
              type === 'high' ? 'text-yellow-400 rotate-180' : 'text-blue-400'
            )}
          />
        </div>
        <div className="w-1/2">
          <span className="font-bold text-gray-600">{percentageText}</span>
        </div>
      </div>
    </div>
  )
}

export default function QuarterLiabilityCard({
  yearKey,
  modifiers,
  quartersByYear,
  quartersByYearHigh,
  quarter
}: {
  yearKey: string
  modifiers: CbamModifiers
  quartersByYear?: Record<string, AggregatedProjection>
  /** High-CSCF quarter projections when user CSCF < 100%; used for high liability row. */
  quartersByYearHigh?: Record<string, AggregatedProjection>
  quarter: string
}) {
  const { t, currencyFormatter2Decimals } = useI18n()
  const projection = quartersByYear?.[quarter]
  const projectionHigh = quartersByYearHigh?.[quarter]
  const quarterModifier = modifiers.euaPriceQuarters[quarter as 'Q1' | 'Q2' | 'Q3' | 'Q4']
  const price =
    quarterModifier.value != null && quarterModifier.value !== ''
      ? parseFloat(quarterModifier.value)
      : 0
  const lowPct =
    quarterModifier.lowPercentage != null && quarterModifier.lowPercentage !== ''
      ? parseFloat(quarterModifier.lowPercentage) / 100
      : 0
  const highPct =
    quarterModifier.highPercentage != null && quarterModifier.highPercentage !== ''
      ? parseFloat(quarterModifier.highPercentage) / 100
      : 0

  const totalEmissions = projection?.totalEmissions || 0
  const freeAllocation = projection?.freeAllocationTCo2 || 0
  const netEmissions = projection?.netEmissions || 0
  const netEmissionsHigh = projectionHigh?.netEmissions ?? netEmissions

  const baseLiability = price > 0 ? computeCbamLiabilityEuro(netEmissions, price) : 0
  const liabilityLow = computeLiabilityLowerBound(baseLiability, lowPct)
  const highBaseLiability = price > 0 ? computeCbamLiabilityEuro(netEmissionsHigh, price) : 0
  const liabilityHigh = computeLiabilityUpperBound(highBaseLiability, highPct)

  const label = `${quarter} ${yearKey}`

  return (
    <div className="bg-white p-2 border border-gray-400">
      <div className="bg-blue-300">
        <h3 className="font-semibold p-2 text-center">{label}</h3>
      </div>
      <div className="bg-green-300 flex items-center p-2">
        <div className="w-1/2 text-right px-4 py-2">
          <span className="font-semibold text-xl">{currencyFormatter2Decimals(baseLiability)}</span>
        </div>
        <div className="w-1/2 px-4 py-2 truncate">
          <span
            title={t('cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.liability')}
            className="text-sm text-text-secondary"
          >
            {t('cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.liability')}
          </span>
        </div>
      </div>
      <LiabilityBoundsRow
        formattedLiability={currencyFormatter2Decimals(liabilityHigh)}
        label={t('cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.high')}
        type="high"
        percentageValue={highPct}
      />
      <LiabilityBoundsRow
        formattedLiability={currencyFormatter2Decimals(liabilityLow)}
        label={t('cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.low')}
        type="low"
        percentageValue={lowPct}
      />
      <Disclosure as="div" className="group">
        {({ open }) => (
          <>
            <div className="flex justify-end px-2 pb-1">
              <DisclosureButton className="inline-flex  relative items-center w-full gap-1 rounded py-0.5 text-sm text-text-secondary hover:cursor-pointer focus:outline-none">
                <span className="text-center w-full">
                  {t(
                    open
                      ? 'cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.hideTotals'
                      : 'cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.showTotals'
                  )}
                </span>
                <div className="absolute right-0">
                  <ChevronDownIcon
                    className="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-data-open:rotate-180"
                    aria-hidden
                  />
                </div>
              </DisclosureButton>
            </div>
            <DisclosurePanel className="border-t border-gray-300">
              <EmissionsRow
                label={t(
                  'cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.includedEmbeddedEmissions'
                )}
                value={totalEmissions}
              />
              <EmissionsRow
                label={t('cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.freeAllocation')}
                value={freeAllocation}
              />
              <EmissionsRow
                label={t(
                  'cbamModifiers:certificatesAndLiabilityPerQuarter.quarterCard.certificatesRequired'
                )}
                value={netEmissions}
              />
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  )
}
